import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { AlertCircle, AlertTriangle } from '@untitled-ui/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ModalPaymentFailed } from '../components/modal-payment-failed';
import { ModalSubscriptionSuccess } from '../components/modal-subscription-success';
import { TopBar, TopBarVariant } from '../components/top-bar';
import { getConfig } from '../config';
import { PlanStatus, SubscriptionPaymentCycle } from '../entity/api/organization-billing';
import { OrganizationUserRole } from '../entity/api/organization-user';
import { MaintenanceSeverity } from '../entity/api/system-config';
import { addTrackCrumb, trackError } from '../error/tracking';
import { getLogger } from '../logger';
import { captureException } from '../logger/capture-exception';
import {
  buildUserData,
  consumeCheckoutPlan,
  isGTMLoaded,
  peekCheckoutPlan,
  toPrice,
  toRenewalType,
} from '../services/gtm';
import { groupPostHogWorkspace, identifyPostHogUser } from '../services/posthog';
import {
  getStores,
  useAuthUser,
  useOrganizationBilling,
  useOrganizationUsersByUserId,
} from '../services/storage';
import { getMaintenanceConfig, useSystemConfigs } from '../services/storage/system-config-queries';
import { UIKey } from '../services/storage/ui-key-value';
import { tNamespace } from '../services/translation';
import { formatDateToDdMmYyyy } from '../shared/date';
import { isMobile } from '../shared/mobile';
import { getSelectedOrganization } from '../shared/organization';
import { capitalize } from '../shared/string';
import { calculateMonthlyPrice } from '../shared/subscription';
import { ContentModalsProvider } from './app/_content-modals-context';

const tBilling = tNamespace('app.billing.general');

type AppSearch = {
  status?: string;
  kind?: string;
  // eslint-disable-next-line camelcase
  session_id?: string;
};

export const Route = createFileRoute('/app')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app',
      level: 'info',
    });
  },
  validateSearch: function (search: Record<string, unknown>): AppSearch {
    return {
      status: typeof search.status === 'string' ? search.status : undefined,
      kind: typeof search.kind === 'string' ? search.kind : undefined,
      // eslint-disable-next-line camelcase
      session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
    };
  },
  // eslint-disable-next-line no-use-before-define
  component: AppLayout,
});

function handleUpdatePayment() {
  // TODO: integrate with API when ready
}

export function AppLayout() {
  const { pathname } = useLocation();
  const config = getConfig();
  const searchParams = Route.useSearch();
  const navigate = useNavigate();

  // Fetch system configs to get maintenance status
  useSystemConfigs();

  // Check if we're on mobile (don't show TopBar on mobile)
  const isMobileDevice = isMobile();

  // Top bar state (shared between maintenance and billing)
  const [topBarVariant, setTopBarVariant] = useState<TopBarVariant>(TopBarVariant.Default);
  const [showTopBar, setShowTopBar] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [shouldDisplayLink, setShouldDisplayLink] = useState(true);
  const [linkMessage, setLinkMessage] = useState<string>('');
  const [canCloseNotification, setCanCloseNotification] = useState(false);
  const [topBarIcon, setTopBarIcon] = useState<React.ReactNode>(
    <AlertTriangle width={16} height={16} />
  );

  // Modal state
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isPaymentFailedModalOpen, setIsPaymentFailedModalOpen] = useState(false);
  const [successModalTitle, setSuccessModalTitle] = useState<string>('');
  const [successModalDescription, setSuccessModalDescription] = useState<string>('');

  // Track which modals have been shown to prevent reopening
  const [hasShownSuccessModal, setHasShownSuccessModal] = useState(false);
  const [hasShownPaymentFailedModal, setHasShownPaymentFailedModal] = useState(false);

  const hasFiredSubscribedEvent = useRef(false);

  // TanStack Query hooks
  const { data: currentUser } = useAuthUser();
  const currentOrg = getSelectedOrganization(currentUser);

  // TanStack Query for org users by org ID
  const { data: userOrgUsersData } = useOrganizationUsersByUserId(currentUser?._id);
  const currentOrgUser = userOrgUsersData?.find(
    (orgUser) => orgUser?.organizationId === currentOrg?._id
  );

  // TanStack Query for organization billing
  const { data: billingStatus } = useOrganizationBilling(currentOrg?._id);

  const isNotificationDismissed = getStores().uiKeyValue((s) =>
    s.getFromStore(UIKey.NOTIFICATION_DISMISSED)
  );

  function handleTopBarButton() {
    if (billingStatus?.currentPlanStatus == null) {
      return;
    }
    if (
      billingStatus.currentPlanStatus === PlanStatus.PastDue ||
      billingStatus.currentPlanStatus === PlanStatus.Cancelling
    ) {
      navigate({
        to: `/app/settings/organization/${currentOrg?._id}/plan`,
        params: { id: currentOrg?._id ?? '' },
      }).catch(function (err) {
        captureException(
          err,
          {
            file: 'routes/app.tsx',
            method: 'handleTopBarButton',
            infoMessage: 'unable to navigate to plan',
          },
          trackError
        );
      });
    }
  }

  const clearUrlParams = useCallback(
    function () {
      navigate({ to: '/app', search: {}, replace: true }).catch(function (err) {
        captureException(
          err,
          {
            file: 'routes/app.tsx',
            method: 'clearUrlParams',
            infoMessage: 'unable to clear URL params',
          },
          trackError
        );
      });
    },
    [navigate]
  );

  // Check for maintenance banner (PRIORITY - overrides billing notifications)
  useEffect(
    function () {
      const maintenanceConfig = getMaintenanceConfig();

      // Check if maintenance is active and has a valid message
      if (
        maintenanceConfig != null &&
        maintenanceConfig.active &&
        maintenanceConfig.message != null &&
        maintenanceConfig.message.trim() !== ''
      ) {
        // Map maintenance severity to TopBar variant and icon
        let variant = TopBarVariant.MaintenanceInfo;
        let icon = <AlertCircle width={16} height={16} />;

        if (maintenanceConfig.severity === MaintenanceSeverity.Warning) {
          variant = TopBarVariant.MaintenanceWarning;
          icon = <AlertCircle width={16} height={16} />;
        } else if (maintenanceConfig.severity === MaintenanceSeverity.Error) {
          variant = TopBarVariant.MaintenanceError;
          icon = <AlertTriangle width={16} height={16} />;
        }

        setTopBarVariant(variant);
        setAlertMessage(maintenanceConfig.message);
        setShouldDisplayLink(false);
        setCanCloseNotification(false);
        setTopBarIcon(icon);
        setShowTopBar(true);
        return; // Exit early - maintenance takes priority over billing
      }

      // If no active maintenance, the billing logic below will handle the top bar
    },
    [] // Re-run when system configs change
  );

  // Determine top bar variant and behavior based on subscription status
  useEffect(
    function () {
      // Check for maintenance first - if active, skip billing notifications
      const maintenanceConfig = getMaintenanceConfig();
      if (
        maintenanceConfig != null &&
        maintenanceConfig.active &&
        maintenanceConfig.message != null &&
        maintenanceConfig.message.trim() !== ''
      ) {
        return; // Maintenance banner is showing, skip billing logic
      }
      // we want to ignore internal pages, like the check session
      if (window.location.href.indexOf('check-checkout-session') !== -1) {
        return;
      }

      if (billingStatus?.currentPlanStatus == null) {
        return;
      }

      const subscriptionStatus = billingStatus.currentPlanStatus;

      // Handle modal opening and URL parameter clearing
      function handleModalOpen(modalType: 'success' | 'paymentFailed') {
        if (modalType === 'success' && !hasShownSuccessModal) {
          setIsSuccessModalOpen(true);
          setHasShownSuccessModal(true);
          clearUrlParams();
        } else if (modalType === 'paymentFailed' && !hasShownPaymentFailedModal) {
          setIsPaymentFailedModalOpen(true);
          setHasShownPaymentFailedModal(true);
          clearUrlParams();
        }
      }

      if (subscriptionStatus === PlanStatus.PastDue) {
        if (currentOrgUser?.role !== OrganizationUserRole.Owner) {
          setShowTopBar(false);
          return;
        }
        if (searchParams.status === 'cancel') {
          handleModalOpen('paymentFailed');
          return;
        }
        if (pathname.includes('/plan')) {
          setShowTopBar(false);
          return;
        }
        setTopBarVariant(TopBarVariant.PaymentFailed);
        setShowTopBar(true);
        setAlertMessage(tBilling('paymentFailedTopBar'));
        setLinkMessage(tBilling('updatePaymentMethod'));
        setTopBarIcon(<AlertTriangle width={16} height={16} />);
        return;
      }

      if (subscriptionStatus === PlanStatus.Cancelling) {
        if (currentOrgUser?.role === OrganizationUserRole.Owner && !pathname.includes('/plan')) {
          setShouldDisplayLink(true);
        } else {
          setShouldDisplayLink(false);
        }
        setCanCloseNotification(true);
        setTopBarVariant(TopBarVariant.PaymentCancelled);
        setShowTopBar(true);
        setAlertMessage(
          tBilling('cancelledTopBar', {
            date: formatDateToDdMmYyyy(new Date(billingStatus.currentPlanEndDate)),
          })
        );
        setLinkMessage(tBilling('reactivatePlan'));
        setTopBarIcon(<AlertTriangle width={16} height={16} />);
        return;
      }

      if (searchParams.kind === 'extra-credits' && searchParams.status === 'credit-success') {
        handleModalOpen('success');
        setSuccessModalTitle(tBilling('creditsSuccess'));
        setSuccessModalDescription(tBilling('creditsSuccessDescription'));
        return;
      }

      if (searchParams.status === 'success') {
        if (!hasFiredSubscribedEvent.current && billingStatus != null && isGTMLoaded()) {
          const checkoutPlan = peekCheckoutPlan();
          const planName =
            billingStatus.plan?.kind ??
            (billingStatus.currentPlanName != null && billingStatus.currentPlanName !== ''
              ? billingStatus.currentPlanName
              : null) ??
            checkoutPlan?.plan ??
            null;
          const hasBillingData = planName != null && planName !== '';

          if (hasBillingData) {
            consumeCheckoutPlan();
            hasFiredSubscribedEvent.current = true;
            const monthlyPrice = calculateMonthlyPrice(
              billingStatus.plan?.pricePerMonth ??
                checkoutPlan?.pricePerMonth ??
                billingStatus.currentMonthlyPriceAmount,
              billingStatus.plan?.priceDiscount ?? checkoutPlan?.priceDiscount ?? 0
            );
            const paymentCycle =
              billingStatus.plan?.paymentCycle ??
              checkoutPlan?.paymentCycle ??
              billingStatus.currentPlanRecurring;
            const currency =
              billingStatus.plan?.currency ??
              checkoutPlan?.currency ??
              billingStatus.currentCurrency;
            const eventName =
              subscriptionStatus === PlanStatus.Trialing ? 'start_trial' : 'subscribe';
            window.dataLayer.push({
              event: eventName,
              plan: planName,
              // eslint-disable-next-line camelcase
              renewal_type: toRenewalType(paymentCycle),
              price: toPrice(monthlyPrice),
              value: toPrice(
                paymentCycle === SubscriptionPaymentCycle.Year ? monthlyPrice * 12 : monthlyPrice
              ),
              currency: currency.toUpperCase(),
              // eslint-disable-next-line camelcase
              predicted_ltv: toPrice(
                paymentCycle === SubscriptionPaymentCycle.Year
                  ? monthlyPrice * 12
                  : config.predictedMonthlyLtv
              ),
              // eslint-disable-next-line camelcase
              transaction_id: searchParams.session_id ?? undefined,
              // eslint-disable-next-line camelcase
              user_id: currentUser?._id,
              organizationId: currentOrg?._id,
              // eslint-disable-next-line camelcase
              user_data: currentUser != null ? buildUserData(currentUser) : undefined,
            });
          }
        } else if (!hasFiredSubscribedEvent.current) {
          getLogger().warn('data layer is not initialized');
          captureException(
            new Error('data layer is not initialized'),
            {
              file: 'routes/app.tsx',
              method: 'handleModalOpen',
              infoMessage: 'data layer is not initialized',
            },
            trackError
          );
        }

        if (subscriptionStatus === PlanStatus.Trialing) {
          handleModalOpen('success');
          setSuccessModalTitle(tBilling('trialSuccess'));
          setSuccessModalDescription(
            tBilling('trialSuccessDescription', { trialDays: config.trialDays })
          );
          return;
        } else if (
          subscriptionStatus === PlanStatus.Subscribed &&
          searchParams.kind === 'subscription_update'
        ) {
          handleModalOpen('success');
          setSuccessModalTitle(tBilling('subscriptionUpdated'));
          setSuccessModalDescription(
            tBilling('subscriptionUpdatedDescriptionImmediately', {
              plan: capitalize(billingStatus.plan?.kind ?? ''),
            })
          );
          return;
        } else if (subscriptionStatus === PlanStatus.Subscribed) {
          handleModalOpen('success');
          setSuccessModalTitle(tBilling('subscriptionSuccess'));
          setSuccessModalDescription(tBilling('subscriptionSuccessDescription'));
          return;
        }
      }
      if (
        subscriptionStatus === PlanStatus.Switched &&
        searchParams.kind === 'subscription_update'
      ) {
        const nextPlan = billingStatus.plan?.kind ?? '';
        handleModalOpen('success');
        setSuccessModalTitle(tBilling('subscriptionUpdated'));
        setSuccessModalDescription(
          tBilling('subscriptionUpdatedDescription', {
            plan: nextPlan,
            date: formatDateToDdMmYyyy(new Date(billingStatus.currentPlanEndDate)),
          })
        );
        return;
      }

      // Always reset top bar to default when not in error states
      setTopBarVariant(TopBarVariant.Default);
      setShowTopBar(false);
    },
    [
      currentOrgUser?.role,
      pathname,
      billingStatus,
      searchParams.status,
      searchParams.kind,
      hasShownSuccessModal,
      hasShownPaymentFailedModal,
      clearUrlParams,
      currentOrg?._id,
    ]
  );

  // Update top banner visibility flag in UI store
  // Don't show TopBar on mobile
  useEffect(
    function () {
      const shouldShowTopBar = showTopBar && !isNotificationDismissed && !isMobileDevice;
      const isTopBannerActive: boolean = shouldShowTopBar;
      getStores()
        .uiKeyValue.getState()
        .setToStore(UIKey.IS_TOP_BANNER_ACTIVE as UIKey, isTopBannerActive);
    },
    [showTopBar, isNotificationDismissed, isMobileDevice]
  );

  // Ensure PostHog person/group properties stay in sync with subscription updates
  // (success, cancellation, plan changes, etc.) without relying on page redirects
  useEffect(
    function () {
      identifyPostHogUser(currentUser, billingStatus);
      groupPostHogWorkspace(currentOrg, billingStatus);
    },
    [billingStatus, currentUser, currentOrg]
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {showTopBar && !isNotificationDismissed && !isMobileDevice ? (
        <TopBar
          icon={topBarIcon}
          variant={topBarVariant}
          onClick={handleTopBarButton}
          alertMessage={alertMessage}
          linkMessage={linkMessage}
          shouldDisplayLink={shouldDisplayLink}
          canCloseNotification={canCloseNotification}
          onCloseNotification={function () {
            getStores().uiKeyValue.getState().setToStore(UIKey.NOTIFICATION_DISMISSED, true);
          }}
        />
      ) : null}
      <div
        className={twMerge(
          'flex-1 overflow-hidden',
          showTopBar && !isMobileDevice ? 'h-[100%-44px]' : ''
        )}
      >
        {/*
          ContentModalsProvider must live here at the /app layout level — not inside
          _sidebar-layout — because /app/content/$id (the editor) is a sibling of
          _sidebar-layout, not a child. Both branches need access to useContentModals()
          (e.g. to open the trial-limit modal on a forbidden status change).
        */}
        <ContentModalsProvider>
          <Outlet />
        </ContentModalsProvider>
      </div>
      <ModalPaymentFailed
        open={isPaymentFailedModalOpen}
        onRetry={() => handleUpdatePayment()}
        onOpenChange={setIsPaymentFailedModalOpen}
      />
      <ModalSubscriptionSuccess
        open={isSuccessModalOpen}
        onOpenChange={setIsSuccessModalOpen}
        title={successModalTitle}
        description={successModalDescription}
      />
    </div>
  );
}
